import { Link } from 'react-router';
import { ThemeToggle } from '@mieweb/ui';
import {
  Apple,
  Monitor,
  Download,
  Clock,
  ArrowRight,
  BellRing,
  RefreshCw,
  AppWindow,
  UserCheck,
} from 'lucide-react';
import { BrandLockup } from '../components/BrandMark';
import { useDesktopLatest } from '../lib/useDesktopLatest';

const FEATURES = [
  {
    icon: BellRing,
    title: 'Native notifications',
    sub: 'Hear about new feedback the moment it lands — even when the window is closed.',
  },
  {
    icon: RefreshCw,
    title: 'Auto updates',
    sub: 'Signed updates install themselves; you are always on the latest version.',
  },
  {
    icon: AppWindow,
    title: 'Dedicated window',
    sub: 'Your feedback workspace in its own app, out of the browser tab pile.',
  },
  {
    icon: UserCheck,
    title: 'Same account',
    sub: 'Sign in with your existing Pollenate login — email code or SSO.',
  },
];

export function DownloadPage() {
  const { os, latest, loading, macHref, winMsiHref, winExeHref } = useDesktopLatest();
  const onWindows = os === 'windows';
  const hasWindows = Boolean(winMsiHref || winExeHref);
  const winPrimary = winMsiHref ?? winExeHref;

  const primaryCta =
    onWindows && winPrimary ? (
      <a
        href={winPrimary}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-primary-600"
      >
        <Monitor className="h-5 w-5" />
        Download for Windows
      </a>
    ) : macHref ? (
      <a
        href={macHref}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-primary-600"
      >
        <Apple className="h-5 w-5" />
        Download for macOS
      </a>
    ) : (
      <span className="inline-flex items-center gap-2 rounded-lg bg-slate-200 dark:bg-neutral-800 px-6 py-3 text-base font-semibold text-slate-500 dark:text-neutral-400">
        <Download className="h-5 w-5" />
        {loading ? 'Checking latest version…' : 'Coming soon'}
      </span>
    );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link
                to="/login"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">
            Desktop app
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white">
            Pollenate on your desktop
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600 dark:text-neutral-400">
            Your whole feedback workspace in a dedicated window — with native
            notifications the moment new feedback arrives.
          </p>
          <div className="mt-8">{primaryCta}</div>
        </div>

        {/* Platform cards */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          {/* macOS */}
          <a
            href={macHref ?? undefined}
            aria-disabled={!macHref}
            className={`group flex items-center gap-4 rounded-xl border p-5 transition-colors ${
              os === 'mac'
                ? 'border-primary-300 dark:border-primary-500 bg-primary/5 hover:bg-primary/10'
                : 'border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-slate-50 dark:hover:bg-neutral-800/60'
            } ${macHref ? '' : 'pointer-events-none opacity-60'}`}
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-neutral-800 text-slate-900 dark:text-white">
              <Apple className="h-6 w-6" />
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                Download for macOS
                {os === 'mac' && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    Your device
                  </span>
                )}
              </span>
              <span className="block text-sm text-slate-500 dark:text-neutral-400">
                .dmg · Apple silicon &amp; Intel
              </span>
            </span>
            <ArrowRight className="ml-auto h-5 w-5 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
          </a>

          {/* Windows */}
          {hasWindows ? (
            <div
              className={`flex flex-col gap-4 rounded-xl border p-5 ${
                os === 'windows'
                  ? 'border-primary-300 dark:border-primary-500 bg-primary/5'
                  : 'border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
              }`}
            >
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-neutral-800 text-slate-900 dark:text-white">
                  <Monitor className="h-6 w-6" />
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                    Download for Windows
                    {os === 'windows' && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        Your device
                      </span>
                    )}
                  </span>
                  <span className="block text-sm text-slate-500 dark:text-neutral-400">
                    64-bit · Windows 10 &amp; 11
                  </span>
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {winMsiHref && (
                  <a
                    href={winMsiHref}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 dark:border-primary-500 bg-primary/5 px-3 py-1.5 text-sm font-medium text-slate-900 dark:text-white transition-colors hover:bg-primary/10"
                  >
                    <Download className="h-4 w-4" />
                    .msi installer
                  </a>
                )}
                {winExeHref && (
                  <a
                    href={winExeHref}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-1.5 text-sm font-medium text-slate-900 dark:text-white transition-colors hover:bg-slate-50 dark:hover:bg-neutral-800"
                  >
                    <Download className="h-4 w-4" />
                    .exe setup
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4 rounded-xl border border-dashed border-slate-300 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/60 p-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-neutral-800 text-slate-400 dark:text-neutral-500">
                <Monitor className="h-6 w-6" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                  Windows
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-950/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    <Clock className="h-3 w-3" />
                    Coming soon
                  </span>
                </span>
                <span className="block text-sm text-slate-500 dark:text-neutral-400">
                  We&apos;re putting the finishing touches on the Windows build.
                </span>
              </span>
            </div>
          )}
        </div>

        {/* What you get */}
        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FEATURES.map(({ icon: Icon, title, sub }) => (
            <div
              key={title}
              className="rounded-xl border border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4"
            >
              <Icon className="h-5 w-5 text-primary" />
              <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-neutral-400">{sub}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500 dark:text-neutral-400">
          <span>Signs in with your existing Pollenate account.</span>
          <span>
            {loading ? (
              <span
                className="inline-block h-4 w-28 animate-pulse rounded bg-slate-200 dark:bg-neutral-800"
                aria-hidden
              />
            ) : latest ? (
              <>
                Latest version:{' '}
                <span className="font-medium text-slate-900 dark:text-white">{latest.version}</span>
              </>
            ) : null}
          </span>
        </div>
      </main>
    </div>
  );
}
